import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Beaker, Cog, FlaskConical, Zap, Search, CheckCircle2, Building2, ChevronRight, Info, X } from "lucide-react";
import LitLabLayout from "../components/LitLabLayout";
import LitLabQrCard from "../components/LitLabQrCard";
import { litlabServices, litlabIndustries, litlabIndustry, litlabArticles } from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LITLAB_TEST_DESCRIPTIONS, LITLAB_CATEGORY_DESCRIPTIONS } from "../data/testDescriptions";

// Slug maps that align frontend test names with backend article slugs.
const TEST_SLUG_MAP = {
  "Metallurgical Failure Analysis": "metallurgical-failure-analysis",
  "Metallography / Photomicrography": "metallography-photomicrography",
  "Microstructure / Grain Size": "microstructure-grain-size",
  "Microhardness (Knoop, Vickers)": "microhardness",
  "Alpha Case (Titanium Alloys)": "alpha-case",
  "Case Depth / Decarburization Evaluations": "case-depth-decarb",
  "Plating / Coating Thickness & Hardness": "plating-coating",
  "Hydrogen Embrittlement": "hydrogen-embrittlement",
  "Intergranular Attack": "intergranular-attack",
  "Corrosion Evaluations": "corrosion",
  "Conductivity Evaluations": "conductivity",
};

// Maps the same test names to the deep-content page slugs stored in
// `litlab_pages` (mirrored from litlab.com's URL structure).
const TEST_PAGE_SLUG_MAP = {
  "Metallurgical Failure Analysis": "metallurgical-failure-analysis",
  "Metallography / Photomicrography": "metallography-photomicrography",
  "Microstructure / Grain Size": "microstructure-grain-size",
  "Microhardness (Knoop, Vickers)": "microhardness-knoop-vickers",
  "Alpha Case (Titanium Alloys)": "alpha-case-titanium-alloys",
  "Case Depth / Decarburization Evaluations": "case-depth-decarburization-evaluations",
  "Plating / Coating Thickness & Hardness": "plating-coating-thickness-hardness",
  "Hydrogen Embrittlement": "hydrogen-embrittlement",
  "Intergranular Attack": "intergranular-attack",
  "Corrosion Evaluations": "corrosion-evaluations",
  "Conductivity Evaluations": "conductivity-evaluations",
  "Tensile / Compression / Proof Load Testing": "tensile-compression-proof-load-testing",
  "Fastener Testing": "fastener-testing",
  "Shear Testing": "shear-testing",
  "Charpy Impact Testing": "charpy-impact-testing",
  "Hardness Testing": "hardness-testing",
  "Bend Testing": "bend-testing",
};

const SERVICE_ICONS = {
  metallurgical: Beaker, mechanical: Cog, chemical: FlaskConical,
  "welder-qualification": Zap, forensic: Search,
};

// -------------------- Services --------------------
export function LitLabServices() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);   // {name, serviceSlug}
  const [activeCategory, setActiveCategory] = useState(null);
  const [articles, setArticles] = useState({ test: {}, category: {} });

  useEffect(() => {
    document.title = "Testing Services | L.I.T. Labs";
    litlabServices().then((d) => setItems(d.items || []));
    // Live CMS articles — the customer can edit these without a deploy
    litlabArticles().then((d) => {
      const idx = { test: {}, category: {} };
      for (const a of (d.articles || [])) {
        if (idx[a.kind]) idx[a.kind][a.slug] = a;
      }
      setArticles(idx);
    }).catch(() => {});
  }, []);

  const activeDesc = active
    ? (articles.test[TEST_SLUG_MAP[active.name]] || LITLAB_TEST_DESCRIPTIONS[active.name])
    : null;
  const activeCatDesc = activeCategory
    ? (articles.category[activeCategory] || LITLAB_CATEGORY_DESCRIPTIONS[activeCategory])
    : null;

  return (
    <LitLabLayout>
      <section className="pt-16 pb-12 bg-white border-b border-sky-200" data-testid="litlab-services">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Capabilities</div>
          <h1 className="mt-3 litlab-serif text-4xl md:text-6xl font-bold text-slate-900 max-w-3xl">
            Testing services, documented to Nadcap discipline.
          </h1>
          <p className="mt-5 text-slate-600 max-w-2xl">
            Five categories, over forty individual test methods. Every result traceable to a
            calibrated instrument and a signed procedure.
          </p>
        </div>
      </section>

      <section className="py-16 bg-sky-50">
        <div className="max-w-6xl mx-auto px-6 space-y-6">
          {items.map((s) => {
            const Icon = SERVICE_ICONS[s.slug] || Beaker;
            return (
              <div key={s.slug} id={s.slug} data-testid={`litlab-service-block-${s.slug}`}
                className="rounded-2xl border border-sky-200 bg-white p-8 lg:p-10 shadow-sm">
                <div className="flex items-start gap-5 flex-wrap">
                  <div className="w-14 h-14 rounded-xl bg-sky-100 border border-sky-300 grid place-items-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-sky-700" />
                  </div>
                  <div className="flex-1 min-w-[280px]">
                    <div className="text-xs uppercase tracking-[0.3em] text-slate-500 font-bold">{s.kicker}</div>
                    {LITLAB_CATEGORY_DESCRIPTIONS[s.slug] ? (
                      <button
                        type="button"
                        onClick={() => setActiveCategory(s.slug)}
                        data-testid={`litlab-category-open-${s.slug}`}
                        className="mt-1 group inline-flex items-center gap-2 text-left"
                      >
                        <h2 className="litlab-serif text-2xl md:text-3xl font-bold text-slate-900 group-hover:text-sky-700 transition">{s.label}</h2>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-sky-700 group-hover:text-sky-800">Learn more <Info className="inline w-3 h-3" /></span>
                      </button>
                    ) : (
                      <h2 className="mt-1 litlab-serif text-2xl md:text-3xl font-bold text-slate-900">{s.label}</h2>
                    )}
                    <p className="mt-3 text-slate-700 leading-relaxed max-w-3xl">{s.description}</p>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {s.tests.map((t) => {
                    const hasDetail = Boolean(LITLAB_TEST_DESCRIPTIONS[t]);
                    const RowInner = (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="flex-1">{t}</span>
                        {hasDetail && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700">
                            Learn more <Info className="w-3 h-3" />
                          </span>
                        )}
                      </>
                    );
                    return hasDetail ? (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setActive({ name: t, serviceSlug: s.slug })}
                        data-testid={`litlab-test-open-${s.slug}-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                        className="flex items-start gap-2 text-left text-sm text-slate-700 py-1.5 border-b border-sky-100 hover:text-sky-800 hover:bg-sky-50/60 -mx-2 px-2 rounded transition"
                      >
                        {RowInner}
                      </button>
                    ) : (
                      <div key={t} className="flex items-start gap-2 text-sm text-slate-700 py-1.5 border-b border-sky-100">
                        {RowInner}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6">
                  <Link to={`/litlab/quote?service=${s.slug}`}
                    data-testid={`litlab-service-quote-${s.slug}`}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-700 hover:text-sky-800">
                    Request a quote for {s.label} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
        <div className="max-w-3xl mx-auto mt-14 px-6">
          <LitLabQrCard
            productionUrl="https://websitetech.pro/litlab/services"
            subtitle="Scan to share our full services catalog"
            testId="services"
          />
        </div>
      </section>

      {/* Test-detail popup — matched to the exact test name in the grid */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-xl bg-white border border-sky-200"
          data-testid="litlab-test-dialog"
        >
          <DialogHeader>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Test method</div>
            <DialogTitle className="litlab-serif text-2xl font-bold text-slate-900" data-testid="litlab-test-dialog-title">
              {activeDesc?.title}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Test method description
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 text-slate-700 leading-relaxed litlab-article-body"
               data-testid="litlab-test-dialog-body"
               dangerouslySetInnerHTML={{ __html: activeDesc?.body || "" }} />
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            {TEST_PAGE_SLUG_MAP[active?.name] && (
              <Link
                to={`/litlab/services/${TEST_PAGE_SLUG_MAP[active?.name]}`}
                onClick={() => setActive(null)}
                data-testid="litlab-test-dialog-fullpage"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-900 bg-sky-100 hover:bg-sky-200 px-3 py-1.5 rounded-md ring-1 ring-sky-200"
              >
                Read the full page <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <Link
              to={`/litlab/quote?service=${active?.serviceSlug || "metallurgical"}&test=${encodeURIComponent(active?.name || "")}`}
              onClick={() => setActive(null)}
              data-testid="litlab-test-dialog-quote"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-700 hover:text-sky-800"
            >
              Request a quote for this test <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category-level overview popup */}
      <Dialog open={!!activeCategory} onOpenChange={(o) => !o && setActiveCategory(null)}>
        <DialogContent
          className="max-w-xl bg-white border border-sky-200"
          data-testid="litlab-category-dialog"
        >
          <DialogHeader>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Capability</div>
            <DialogTitle className="litlab-serif text-2xl font-bold text-slate-900" data-testid="litlab-category-dialog-title">
              {activeCatDesc?.title}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Capability overview
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 text-slate-700 leading-relaxed litlab-article-body"
               data-testid="litlab-category-dialog-body"
               dangerouslySetInnerHTML={{ __html: activeCatDesc?.body || "" }} />
          <div className="mt-3 flex flex-wrap gap-3">
            <Link
              to={`/litlab/quote?service=${activeCategory || ""}`}
              onClick={() => setActiveCategory(null)}
              data-testid="litlab-category-dialog-quote"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-700 hover:text-sky-800"
            >
              Request a quote for this capability <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </LitLabLayout>
  );
}

// -------------------- Industries index --------------------
export function LitLabIndustries() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    document.title = "Industries we serve | L.I.T. Labs";
    litlabIndustries().then((d) => setItems(d.items || []));
  }, []);
  return (
    <LitLabLayout>
      <section className="pt-16 pb-12 bg-white border-b border-sky-200" data-testid="litlab-industries">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Who we work with</div>
          <h1 className="mt-3 litlab-serif text-4xl md:text-6xl font-bold text-slate-900 max-w-3xl">
            Industries where a wrong answer costs more than a right one.
          </h1>
        </div>
      </section>
      <section className="py-16 bg-sky-50">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((i) => (
            <Link key={i.slug} to={`/litlab/industries/${i.slug}`}
              data-testid={`litlab-industries-card-${i.slug}`}
              className="group rounded-xl border border-sky-200 bg-white hover:border-sky-500 hover:shadow-md p-6 transition">
              <div className="w-10 h-10 rounded-lg bg-sky-100 border border-sky-300 grid place-items-center">
                <Building2 className="w-5 h-5 text-sky-700" />
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-slate-500 font-bold">{i.kicker}</div>
              <div className="mt-1 text-xl font-bold text-slate-900 group-hover:text-sky-700 transition litlab-serif">{i.label}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-sky-700 group-hover:gap-2 transition-all">
                Read more <ChevronRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </LitLabLayout>
  );
}

// -------------------- Industry detail --------------------
export function LitLabIndustryDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    litlabIndustry(slug).then((d) => {
      setData(d);
      document.title = `${d.industry.label} | L.I.T. Labs`;
    }).catch(() => setErr(true));
  }, [slug]);
  if (err) return <LitLabLayout><div className="pt-32 text-center text-slate-500">Industry not found.</div></LitLabLayout>;
  if (!data) return <LitLabLayout><div className="pt-20 text-center text-slate-500">Loading…</div></LitLabLayout>;
  const i = data.industry;
  const paragraphs = Array.isArray(i.body_paragraphs) && i.body_paragraphs.length
    ? i.body_paragraphs
    : [i.detail];
  const services = Array.isArray(i.services_offered) && i.services_offered.length
    ? i.services_offered
    : (i.capabilities || []);
  const gallery = Array.isArray(i.gallery) ? i.gallery : (i.image ? [i.image] : []);

  return (
    <LitLabLayout>
      {/* Hero */}
      <section className="pt-16 pb-16 bg-white border-b border-sky-200" data-testid={`litlab-industry-${slug}`}>
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3">
            <Link to="/litlab/industries" className="text-xs uppercase tracking-[0.3em] font-bold text-sky-700 inline-flex items-center gap-1 hover:text-sky-800">
              <ArrowLeft className="w-3 h-3" /> All industries
            </Link>
            <div className="mt-4 text-xs uppercase tracking-[0.3em] text-slate-500 font-bold">{i.kicker}</div>
            <h1 className="mt-2 litlab-serif text-4xl md:text-6xl font-bold text-slate-900" data-testid={`industry-title-${slug}`}>{i.label}</h1>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">{i.hero}</p>
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed max-w-2xl">
              {paragraphs.map((p, idx) => (
                <p key={idx} data-testid={`industry-body-${slug}-${idx}`}>{p}</p>
              ))}
            </div>
          </div>
          {i.image && (
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden border border-sky-200 shadow-md bg-slate-100">
                <img src={i.image} alt={i.image_alt || i.label}
                  className="w-full aspect-[4/3] object-cover"
                  data-testid={`industry-hero-image-${slug}`}
                  onError={(e) => { e.currentTarget.style.opacity = "0.15"; }} />
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-400 text-center">
                Photography from L.I.T. Labs
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Services offered */}
      <section className="py-16 bg-sky-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">What we do for {i.label}</div>
          <h2 className="mt-2 litlab-serif text-2xl md:text-3xl font-bold text-slate-900">Testing services tailored to your industry.</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
            {services.map((s, idx) => (
              <div key={idx} className="rounded-lg border border-sky-200 bg-white p-4 flex items-start gap-3 shadow-sm hover:border-sky-500 hover:shadow-md transition"
                data-testid={`industry-service-${slug}-${idx}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-1" />
                <div className="text-sm text-slate-700">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 1 && (
        <section className="py-14 border-t border-sky-200 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">From the lab floor</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {gallery.map((src, idx) => (
                <img key={idx} src={src} alt={`${i.label} — L.I.T. Labs ${idx + 1}`}
                  className="rounded-lg border border-sky-200 aspect-[4/3] object-cover w-full"
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related services + CTA */}
      <section className="py-16 border-t border-sky-200 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl">
            <LitLabQrCard
              productionUrl={`https://websitetech.pro/litlab/industries/${slug}`}
              subtitle={`Scan to share the ${i.label} page with a client on the shop floor`}
              testId={`industry-${slug}`}
            />
          </div>
          <div className="mt-10 text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Explore testing capabilities</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {data.related_services.map((s) => (
              <Link key={s.slug} to={`/litlab/services#${s.slug}`}
                className="text-xs uppercase tracking-widest font-bold border border-sky-300 hover:border-sky-500 bg-sky-50 text-slate-700 hover:text-sky-700 rounded px-3 py-1.5 transition">
                {s.label}
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/litlab/quote?industry=${i.slug}`}
              data-testid="litlab-industry-quote"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white px-6 py-3 rounded font-bold transition shadow-sm">
              Request a quote for {i.label} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/litlab/contact"
              className="inline-flex items-center gap-2 border border-sky-300 hover:border-sky-500 bg-white text-slate-700 hover:text-slate-900 px-6 py-3 rounded font-semibold transition">
              Ask a metallurgist
            </Link>
          </div>
        </div>
      </section>
    </LitLabLayout>
  );
}
